import {Divider, ListItem, ListItemText} from '@mui/material'
import {memo, useMemo} from 'react'
import {useTranslation} from 'react-i18next'
import type {Iri} from '../../../../@types/Api'
import type {VisitPartial} from '../../../../@types/entity/Visit'
import {SpotLabel} from '../../../../components/Label/SpotLabel'
import {TextSkeleton} from '../../../../components/Skeleton/TextSkeleton'
import {useSpotGet} from '../../../../hooks/entity/useSpot'
import {useVisitCollection, useVisitFilterFromCurrentUser} from '../../../../hooks/entity/useVisit'
import {getSpotUrl} from '../../../../hooks/useSpotNavigate'
import {useCurrentSpotIri} from '../../../../states/currentSpot'
import {NavGroup} from './NavGroup'
import {NavLink} from './NavLink'
import {VisitStarButton} from './VisitStarButton'

interface SpotNavLinkProps {
  spotIri: Iri
}

const SpotNavLink = memo(({spotIri}: SpotNavLinkProps) => {
  const {spot, error} = useSpotGet(spotIri)

  if (!spot) {
    return <ListItem>
      <ListItemText primary={<TextSkeleton width={100} error={error}/>}/>
    </ListItem>
  }

  return <NavLink
    to={getSpotUrl(spot.slug)}
    label={<SpotLabel spotIri={spotIri}/>}
    secondaryAction={<VisitStarButton spot={spot}/>}
  />
})

export const MenuSwitch = memo(() => {
  const {t} = useTranslation()
  const {visits} = useVisitCollection({...useVisitFilterFromCurrentUser(), stared: true})
  const currentSpotIri = useCurrentSpotIri()

  const otherVisits = useMemo((): VisitPartial[] | undefined => {
    if (!visits) {
      return undefined
    }

    return visits.member
      .filter((visit) => visit.spot !== currentSpotIri)
  }, [currentSpotIri, visits])

  if (!visits || !otherVisits || otherVisits.length === 0) {
    return null
  }

  return <>
    <Divider/>
    <NavGroup title={t('common.menu.global.stared')}>
      {visits.member.map((visit) => <SpotNavLink
        key={visit['@id']}
        spotIri={visit.spot}
      />)}
    </NavGroup>
  </>
})
